import { gsap } from "gsap";
import type { ScoreResult } from "../types";
import { VERSION_LABEL } from "../constants";
import { ScoreRecords } from "./ScoreRecords";
import { GlobalRanking, type RankingView, type RankedRecord } from "../net/GlobalRanking";

type PanelName = "start" | "pause" | "gameover" | "victory" | "ranking" | null;
type ScorePanelName = "game-over" | "victory";
type ToastPayload = string | { text: string; tone?: "energy" | "champion" | "warning" };

export class OverlayUi {
  private overlay = document.getElementById("ui-overlay") as HTMLElement;
  private toastContainer = this.ensureToastContainer();
  private activePanel: PanelName = "start";
  // Where VOLVER returns from the ranking screen (the panel that opened it).
  private rankingReturnTo: "gameover" | "victory" = "gameover";
  // Alias whose row is highlighted (.is-you) when the ranking screen opens.
  private lastSavedAlias = ScoreRecords.lastName();
  private panels = {
    start: document.getElementById("start-panel") as HTMLElement,
    pause: document.getElementById("pause-panel") as HTMLElement,
    gameover: document.getElementById("game-over-panel") as HTMLElement,
    victory: document.getElementById("victory-panel") as HTMLElement,
    ranking: document.getElementById("ranking-panel") as HTMLElement
  };
  // Start-panel loading bar: filled by real asset progress (boot:progress) and
  // swapped for the INICIAR RUN button once the game is ready (ui:show-start).
  private startLoader = document.getElementById("start-loader");
  private startLoaderFill = document.getElementById("start-loader-fill");
  private startLoaderLabel = document.getElementById("start-loader-label");
  private startButton = document.getElementById("start-button") as HTMLButtonElement | null;
  private startReady = false;

  constructor(private game: Phaser.Game) {
    document.getElementById("start-button")?.addEventListener("click", () => this.game.events.emit("ui:start"));
    document.getElementById("retry-button")?.addEventListener("click", () => this.game.events.emit("ui:start"));
    document.getElementById("again-button")?.addEventListener("click", () => this.game.events.emit("ui:start"));
    // Pause menu — CONTINUAR reuses the existing pause-toggle resume path; the
    // other two are new run-leaving handlers in RunScene.
    document.getElementById("resume-button")?.addEventListener("click", () => this.game.events.emit("game:pause-toggle"));
    document.getElementById("restart-button")?.addEventListener("click", () => this.game.events.emit("game:restart"));
    document.getElementById("home-button")?.addEventListener("click", () => this.game.events.emit("game:home"));
    this.bindScorePanel("game-over");
    this.bindScorePanel("victory");
    // While the alias field is focused, Phaser's global keyboard capture would
    // swallow W/A/S/D and the arrows (preventDefault), so those keys never reach
    // the <input>. Disable the keyboard manager on focus and restore it on blur.
    // The run has already ended when these panels show, so the wolf controls are
    // idle here — toggling has no gameplay effect.
    this.bindAliasKeyboardGuard("game-over");
    this.bindAliasKeyboardGuard("victory");
    // Ranking lives on its own screen. RANKING opens it (remembering the panel
    // we came from); VOLVER returns there; MENU reuses the start path (game:home).
    document.getElementById("game-over-ranking")?.addEventListener("click", () => void this.openRanking("gameover"));
    document.getElementById("victory-ranking")?.addEventListener("click", () => void this.openRanking("victory"));
    document.getElementById("ranking-back")?.addEventListener("click", () => this.show(this.rankingReturnTo));
    document.getElementById("game-over-menu")?.addEventListener("click", () => this.game.events.emit("game:home"));
    document.getElementById("victory-menu")?.addEventListener("click", () => this.game.events.emit("game:home"));

    // Real asset-load progress fills the start-panel bar.
    this.game.events.on("boot:progress", (value: number) => this.updateBootProgress(value));
    // ui:show-start fires after RunScene.create() has attached the ui:start
    // listener — the only point where enabling INICIAR RUN is guaranteed not to
    // produce a dead button. The bar is swapped for the button here.
    this.game.events.on("ui:show-start", () => {
      this.revealStartButton();
      this.show("start");
    });
    this.game.events.on("ui:hide", () => this.show(null));
    // Pause overlay is driven by the same hud:pause signal as the HUD button, so
    // CONTINUAR and the HUD button share one resume path (no divergent toggle).
    this.game.events.on("hud:pause", (paused: boolean) => {
      if (paused) this.show("pause");
      else if (this.activePanel === "pause") this.show(null);
    });
    this.game.events.on("ui:toast", (payload: ToastPayload) => this.showToast(payload));
    this.game.events.on("ui:gameover", (result: ScoreResult) => {
      this.renderScorePanel("game-over", result);
      this.show("gameover");
    });
    this.game.events.on("ui:victory", (result: ScoreResult) => {
      this.renderScorePanel("victory", result);
      this.show("victory");
    });

    // Version stamp (single source: VERSION_LABEL) on every stopped screen.
    this.stampVersion();
    // iOS-only silent-mode reminder on the start panel.
    this.applyIosSilentNotice();
  }

  // Fills every [data-version] placeholder (start / pause / game-over / victory)
  // from the single VERSION_LABEL constant so the string is never hardcoded twice.
  private stampVersion() {
    document.querySelectorAll<HTMLElement>("[data-version]").forEach((element) => {
      element.textContent = VERSION_LABEL;
    });
  }

  // iPhone/iPad mutes web audio under the hardware silent switch, so iOS players
  // can launch the run and hear nothing. Show a discreet reminder only on iOS;
  // Android/desktop never see it.
  private applyIosSilentNotice() {
    const notice = document.getElementById("ios-silent-notice");
    if (notice && this.isIos()) notice.classList.remove("is-hidden");
  }

  // Standard iOS detection. Modern iPadOS reports as "MacIntel" with a real
  // touch screen, so that case is treated as iOS too.
  private isIos(): boolean {
    const nav = window.navigator;
    const ua = nav.userAgent || "";
    const isAppleMobile = /iPad|iPhone|iPod/.test(ua);
    const isIpadOs = nav.platform === "MacIntel" && nav.maxTouchPoints > 1;
    return isAppleMobile || isIpadOs;
  }

  // Updates the loading bar from the real loader progress (0..1). Ignored once
  // the button is live, so a late progress event can't reopen the bar.
  private updateBootProgress(value: number) {
    if (this.startReady) return;
    const percent = Math.round(Math.min(1, Math.max(0, value)) * 100);
    if (this.startLoaderFill) this.startLoaderFill.style.width = `${percent}%`;
    if (this.startLoaderLabel) this.startLoaderLabel.textContent = `CARGANDO… ${percent}%`;
    this.startLoader?.setAttribute("aria-valuenow", String(percent));
  }

  // Assets are ready and RunScene is listening for ui:start: fill the bar, fade
  // it out, then reveal and enable the button (the user gesture that starts audio).
  private revealStartButton() {
    if (this.startReady) return;
    this.startReady = true;
    if (this.startLoaderFill) this.startLoaderFill.style.width = "100%";
    if (this.startLoaderLabel) this.startLoaderLabel.textContent = "CARGANDO… 100%";
    this.startLoader?.classList.add("is-hidden");
    if (this.startButton) {
      this.startButton.classList.remove("is-hidden");
      this.startButton.removeAttribute("disabled");
    }
    // Carga muy rapida: el swap es instantaneo y la transicion CSS evita el
    // parpadeo. Si tras probar molestara, descomentar para forzar un minimo:
    // window.setTimeout(() => { /* reveal button here */ }, 300);
  }

  // Toggles Phaser's global keyboard manager. `false` lets WASD/arrows reach an
  // HTML <input>; `true` restores the in-game wolf controls.
  private setGameKeyboard(enabled: boolean) {
    const keyboard = this.game.input?.keyboard;
    if (keyboard) keyboard.enabled = enabled;
  }

  private bindAliasKeyboardGuard(prefix: ScorePanelName) {
    const input = document.getElementById(`${prefix}-name`) as HTMLInputElement | null;
    if (!input) return;
    input.addEventListener("focus", () => this.setGameKeyboard(false));
    input.addEventListener("blur", () => this.setGameKeyboard(true));
  }

  show(name: PanelName) {
    // Belt-and-suspenders: any panel navigation (restart / home / hide) restores
    // the in-game keyboard, in case a blur didn't fire before leaving the field.
    this.setGameKeyboard(true);
    this.activePanel = name;
    if (!name) {
      gsap.to(this.overlay, {
        autoAlpha: 0,
        duration: 0.22,
        ease: "power2.out",
        onComplete: () => this.overlay.classList.remove("is-visible")
      });
      return;
    }

    this.overlay.classList.add("is-visible");
    Object.entries(this.panels).forEach(([panelName, panel]) => {
      panel.classList.toggle("is-active", panelName === name);
    });
    gsap.fromTo(this.overlay, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.28, ease: "power2.out" });
    gsap.fromTo(this.panels[name], { y: 16, scale: 0.98 }, { y: 0, scale: 1, duration: 0.34, ease: "power3.out" });
  }

  private bindScorePanel(prefix: ScorePanelName) {
    const button = document.getElementById(`${prefix}-save`);
    button?.addEventListener("click", () => {
      const input = document.getElementById(`${prefix}-name`) as HTMLInputElement | null;
      const resultRaw = button.getAttribute("data-score-result");
      if (!input || !resultRaw) return;
      const result = JSON.parse(resultRaw) as ScoreResult;
      // Empty field -> auto-generate a valid random alias (e.g. LOBO4821).
      const alias = input.value.trim() ? input.value : ScoreRecords.randomAlias();
      const outcome = ScoreRecords.save(alias, result);
      // Reflect the final (sanitized) alias back into the field so the player
      // sees exactly what was saved/uploaded.
      input.value = outcome.record?.alias ?? outcome.previous?.alias ?? alias;
      // Remember whose row to highlight when the ranking screen is opened next.
      this.lastSavedAlias = outcome.record?.alias ?? outcome.previous?.alias ?? this.lastSavedAlias;
      if (outcome.accepted) {
        this.setText(
          `${prefix}-rank`,
          outcome.rank > 0 ? `NUEVO RECORD #${outcome.rank}` : "RECORD PERSONAL GUARDADO"
        );
        // Beat personal best -> push to the global ranking. Fire-and-forget so
        // the UI never blocks; a failed upload only shows a discreet toast (the
        // local record is already saved, so game-over stays intact).
        if (outcome.record) {
          void GlobalRanking.upload(outcome.record).then((ok) => {
            if (!ok) this.showToast({ text: "No se pudo subir al ranking global", tone: "warning" });
          });
        }
      } else {
        const best = Math.floor(outcome.previous?.score ?? 0).toLocaleString("en-US");
        this.setText(`${prefix}-rank`, `NO SUPERASTE TU RECORD (MEJOR: ${best})`);
      }
      button.setAttribute("disabled", "true");
      button.textContent = outcome.accepted ? "GUARDADO" : "SIN CAMBIOS";
    });
  }

  private renderScorePanel(prefix: ScorePanelName, result: ScoreResult) {
    const score = Math.floor(result.total);
    // Victory is a triumphant achievement screen: a fixed "¡LO LOGRASTE!" title
    // with the completed-time kept as a discreet subtitle (so the 120s data is
    // not lost). Game-over keeps its original "Sobreviviste Xs" title untouched.
    if (prefix === "victory") {
      this.setText("victory-title", "¡LO LOGRASTE!");
      this.setText("victory-subtitle", `RUN COMPLETA · ${result.elapsed.toFixed(2)}s`);
    } else {
      this.setText(`${prefix}-title`, `Sobreviviste ${result.elapsed.toFixed(2)}s`);
    }
    this.setText(`${prefix}-score`, score.toLocaleString("en-US"));
    this.setText(`${prefix}-distance`, `${Math.floor(result.distance)}m`);
    this.setText(`${prefix}-rank-name`, result.rankName.toUpperCase());
    this.setText(`${prefix}-rank-progress`, result.rankProgressText);

    const input = document.getElementById(`${prefix}-name`) as HTMLInputElement | null;
    const button = document.getElementById(`${prefix}-save`);
    // First-time players -> empty field shows the placeholder; returning players
    // -> their last alias is pre-filled (storedName has no defaultName fallback).
    if (input) input.value = ScoreRecords.storedName() ?? "";
    this.lastSavedAlias = ScoreRecords.lastName();
    if (button) {
      button.removeAttribute("disabled");
      button.setAttribute("data-score-result", JSON.stringify(result));
      button.textContent = "GUARDAR RECORD";
    }
    this.setText(`${prefix}-rank`, ScoreRecords.canEnterTop(score, input?.value ?? "") ? "ENTRA AL RANKING" : "SIGUE CAZANDO EL RECORD");
  }

  private async openRanking(from: "gameover" | "victory") {
    this.rankingReturnTo = from;
    // Show the panel immediately with a loading state, then fill from Supabase.
    // This screen shows ONLY the global Top 10; the local ranking still exists
    // internally (ScoreRecords) but is not displayed here.
    this.setLeaderboardMessage("ranking-leaderboard", "CARGANDO RANKING GLOBAL...");
    this.show("ranking");
    // Identity by alias via storedName() (null when never saved) — avoids the
    // "LOBO" defaultName false-matching another player's row.
    const view = await GlobalRanking.fetchRankingView(ScoreRecords.storedName());
    // The player may have navigated away (VOLVER/MENU) while the request was
    // in flight — don't clobber whatever panel is active now.
    if (this.activePanel !== "ranking") return;
    if (view === null) {
      this.setLeaderboardMessage("ranking-leaderboard", "RANKING NO DISPONIBLE");
      return;
    }
    this.renderRankingView(view);
  }

  private setLeaderboardMessage(listId: string, message: string) {
    const list = document.getElementById(listId);
    if (!list) return;
    list.innerHTML = "";
    const item = document.createElement("li");
    item.className = "leaderboard-empty";
    item.textContent = message;
    list.appendChild(item);
  }

  // Renders the global ranking screen. Default: the full Top 10. When the player
  // sits outside the Top 10, the top section is trimmed to Top 5 and a "mi
  // posición" zone is appended (separator + 2 neighbors + the player's own row +
  // distance line) so everything — including VOLVER — fits portrait without scroll.
  private renderRankingView(view: RankingView) {
    const list = document.getElementById("ranking-leaderboard");
    if (!list) return;
    list.innerHTML = "";
    const header = document.createElement("li");
    header.className = "leaderboard-header";
    header.innerHTML = "<span>#</span><span>Jugador</span><span>Rango</span><span>Score</span>";
    list.appendChild(header);

    if (!view.top10.length) {
      this.appendRows(list, [{ schemaVersion: 0, id: "empty", alias: "SIN RECORDS", deviceId: "", score: 0, elapsed: 0, distance: 0, victory: false, createdAt: "", position: 1 }]);
      return;
    }

    // Highlight by the server's canonical alias for the player (null -> no row
    // highlighted, e.g. a player with no saved record).
    const myAlias = view.me?.alias;
    const showZone = !!(view.me && view.me.position > 10);
    // Trim the top section to Top 5 only when the zone is shown; otherwise keep
    // the full Top 10 exactly as before.
    this.appendRows(list, showZone ? view.top10.slice(0, 5) : view.top10, myAlias);

    if (view.me && view.me.position > 10) {
      const separator = document.createElement("li");
      separator.className = "leaderboard-zone-sep";
      separator.textContent = "— TU POSICIÓN —";
      list.appendChild(separator);

      // The 2 neighbors immediately above me (closest positions). Filter to
      // pos > 10 first so none duplicates the Top 5, then take the last two.
      const neighbors = view.neighbors.filter((n) => n.position > 10).slice(-2);
      const zone = [...neighbors, view.me];
      this.appendRows(list, zone, myAlias);

      const score10 = view.top10[9]?.score ?? 0;
      const gap = Math.floor(score10) - Math.floor(view.me.score);
      const note = document.createElement("li");
      note.className = "leaderboard-gap-note";
      note.textContent = gap > 0 ? `A ${gap.toLocaleString("en-US")} puntos del TOP 10` : "EMPATAS CON EL TOP 10";
      list.appendChild(note);
    }
  }

  // Appends leaderboard rows using each record's explicit `position` (#/Jugador/
  // Rango/Score). The row whose alias matches highlightAlias gets .is-you.
  private appendRows(list: HTMLElement, records: RankedRecord[], highlightAlias?: string) {
    records.forEach((record) => {
      const item = document.createElement("li");
      if (highlightAlias && record.alias === highlightAlias) item.className = "is-you";
      item.innerHTML = [
        `<span>${String(record.position).padStart(2, "0")}</span>`,
        `<span>${record.alias}</span>`,
        `<span>${record.rankName ?? (record.victory ? "Lobo" : "Cachorro")}</span>`,
        `<strong>${Math.floor(record.score).toLocaleString("en-US")}</strong>`
      ].join("");
      list.appendChild(item);
    });
  }

  private showToast(payload: ToastPayload) {
    const text = typeof payload === "string" ? payload : payload.text;
    const tone = typeof payload === "string" ? "energy" : payload.tone ?? "energy";
    const toast = document.createElement("div");
    toast.className = `toast ${tone === "champion" ? "is-champion" : tone === "warning" ? "is-danger" : ""}`.trim();
    toast.textContent = text;
    this.toastContainer.appendChild(toast);
    window.setTimeout(() => toast.remove(), 2100);
  }

  private ensureToastContainer() {
    const existing = document.getElementById("toast-container");
    if (existing) return existing;
    const container = document.createElement("div");
    container.id = "toast-container";
    document.querySelector(".game-frame")?.appendChild(container);
    return container;
  }

  private setText(id: string, text: string) {
    const element = document.getElementById(id);
    if (element) element.textContent = text;
  }
}
